import { useCallback, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { EventInteractionArgs } from 'react-big-calendar/lib/addons/dragAndDrop';

import {
  eventsSnackBarAtom,
  suggestAllAIPlanStateAtom,
  useCalendarContext,
  useEventActions,
  useEvents,
  useRecurringEventScopeModal,
} from '../data-access';
import {
  CALENDAR_STEP_MIN,
  RBCEvent,
  transformEventToRBCEvent,
  transformRBCEventToCalendarEvent,
} from '../components';
import { canManageCalendarEventRecurrence } from '../components/CalendarView/EventMenu/helpers';
import { isAppleEvent, isTemporalAutoScheduledEvent } from '../helpers';
import { isAppleCalendarDesktopSupported } from '../helpers/appleCalendarDesktop.helpers';
import { trackEventMixpanel } from '../utils';
import { useRecoilState } from 'recoil';
import { aiScheduledActionsAtom } from '../data-access/recoil';
import { useEditMeal, EditMealParam } from './useEditMeal';
import { useEditSleep } from './useEditSleep';
import { useAllPlanSuggestedModal } from './useAllPlanSuggestedModal';
import { getMealTypeFromName, isFutureDate, SleepWakeTimeISOStringSet } from '../data-access';
import { translateI18n } from '../i18n';

export const useEventDragAndDrop = () => {
  const [, setEventsSnackbar] = useRecoilState(eventsSnackBarAtom);
  const [, setSuggestAllAIPlanState] = useRecoilState(suggestAllAIPlanStateAtom);
  const [aiScheduledActions, setAiScheduledActions] = useRecoilState(aiScheduledActionsAtom);
  const { calendarEvents } = useEvents();
  const { findCalendarItem, checkCalendarHassAccessToModifyEvent, calendarPreferences } =
    useCalendarContext();
  const [events, setEvents] = useState<RBCEvent[]>([]);
  const previousEventState = useRef<RBCEvent | null>(null);
  const { handleEditMealData, handleEditMealPrediction } = useEditMeal({
    date: dayjs(),
  });
  const { rerunAllSuggestions } = useAllPlanSuggestedModal();
  const { handleEditSleepData, handleEditSleepPrediction } = useEditSleep({
    date: dayjs(),
    onAfterEdit: rerunAllSuggestions,
  });
  const { updateCalendarEvent, eventUpdatingStatus } = useEventActions();
  const { requestRecurringEventScope } = useRecurringEventScopeModal();
  const isAppleDesktopSupported = isAppleCalendarDesktopSupported();

  const handleMealEventUpdate = useCallback(
    (targetEvent: RBCEvent, start: Date, end: Date) => {
      const mealType = getMealTypeFromName(targetEvent.title);
      if (mealType) {
        const newData: EditMealParam = {
          mealType,
          startTime: dayjs(start).toISOString(),
          endTime: dayjs(end).toISOString(),
        };
        const isFutureEvent = isFutureDate(dayjs(targetEvent.start).toISOString(), 'hour');

        if (isFutureEvent) {
          handleEditMealPrediction({
            newData,
            shouldUpdateEvent: true,
            baseCalendarId: targetEvent.calendarId,
          });
        } else {
          handleEditMealData(newData);
        }
      }
    },
    [handleEditMealData, handleEditMealPrediction]
  );

  const handleSleepEventUpdate = useCallback(
    (targetEvent: RBCEvent, start: Date, end: Date) => {
      const newData: SleepWakeTimeISOStringSet = {
        sleepTime: dayjs(start).toISOString(),
        wakeTime: dayjs(end).toISOString(),
      };
      const isFutureEvent = isFutureDate(dayjs(targetEvent.start).toISOString(), 'hour');

      if (isFutureEvent) {
        handleEditSleepPrediction({
          newData,
          shouldUpdateEvent: true,
          baseCalendarId: targetEvent.calendarId,
        });
      } else {
        handleEditSleepData(newData);
      }
    },
    [handleEditSleepData, handleEditSleepPrediction]
  );

  useEffect(() => {
    const filteredEvents = calendarEvents.filter((e) => {
      if (
        calendarPreferences.hideAutoSchedule?.routine === true &&
        (e.autoScheduleFrom?.from === 'routine' || !!e.routineFrom?.routineActivityId)
      ) {
        return false;
      }
      return true;
    });

    const transformedEvents = filteredEvents.map((e) =>
      transformEventToRBCEvent(e, findCalendarItem(e.calendarId))
    );

    setEvents([...transformedEvents]);
  }, [
    JSON.stringify(calendarEvents),
    calendarPreferences.hideAutoSchedule?.routine,
    findCalendarItem,
  ]);

  const handleUndo = useCallback(() => {
    if (!previousEventState.current) {
      return;
    }

    const previousEvent = previousEventState.current;
    void updateCalendarEvent({
      calendarId: previousEvent.calendarId,
      eventId: previousEvent.eventId,
      existingEvent: transformRBCEventToCalendarEvent(previousEvent),
      newEventOption: {
        start: {
          date: previousEvent.start.toISOString(),
          timeZone: dayjs.tz.guess(),
        },
        end: {
          date: previousEvent.end.toISOString(),
          timeZone: dayjs.tz.guess(),
        },
        unSyncToCalendar: !!previousEvent.unSyncToCalendar,
      },
    });
    previousEventState.current = null;
  }, [updateCalendarEvent]);

  useEffect(() => {
    if (!previousEventState.current) {
      return;
    }

    if (eventUpdatingStatus === 'success') {
      setEventsSnackbar({
        message: translateI18n('calendar.snackbar.eventUpdated'),
        actionLabel: translateI18n('calendar.common.undo'),
        onPress: handleUndo,
        autoHideDuration: 4000,
        type: 'success',
      });
    } else if (eventUpdatingStatus === 'pending') {
      setEventsSnackbar({ message: translateI18n('calendar.snackbar.eventUpdating'), type: 'loading' });
    } else if (eventUpdatingStatus === 'error') {
      setEventsSnackbar({ message: translateI18n('calendar.snackbar.updateFailed'), type: 'error' });
    }
  }, [eventUpdatingStatus, handleUndo, setEventsSnackbar]);

  const updateDraggedEvent = useCallback(
    async ({
      targetEvent,
      start,
      end,
      recurringUpdateSpan,
      allowUndo,
    }: {
      targetEvent: RBCEvent;
      start: Date;
      end: Date;
      recurringUpdateSpan?: 'thisEvent' | 'allEvents';
      allowUndo: boolean;
    }) => {
      await updateCalendarEvent({
        calendarId: targetEvent.calendarId!,
        eventId: targetEvent.eventId,
        existingEvent: transformRBCEventToCalendarEvent(targetEvent),
        recurringUpdateSpan,
        originalOccurrenceDate: dayjs(targetEvent.start).toISOString(),
        newEventOption: {
          start: {
            date: dayjs(start).toISOString(),
            timeZone: dayjs.tz.guess(),
          },
          end: {
            date: dayjs(end).toISOString(),
            timeZone: dayjs.tz.guess(),
          },
          unSyncToCalendar: !!targetEvent.unSyncToCalendar,
          taskFrom: targetEvent.taskFrom,
          summary: targetEvent.title,
        },
      });

      previousEventState.current = allowUndo ? targetEvent : null;
    },
    [updateCalendarEvent]
  );

  const maybeGetRecurringProviderScope = useCallback(
    async (targetEvent: RBCEvent): Promise<'thisEvent' | 'allEvents' | 'cancel' | undefined> => {
      const calendarType = findCalendarItem(targetEvent.calendarId!)?.calendarType;
      const isRecurringProviderEvent =
        canManageCalendarEventRecurrence(calendarType, isAppleDesktopSupported) &&
        !!targetEvent.recurrence?.string?.trim();

      if (!isRecurringProviderEvent) {
        return undefined;
      }

      return requestRecurringEventScope({
        title: translateI18n('calendar.recurringScope.title'),
        message: translateI18n('calendar.recurringScope.updateMessage'),
      });
    },
    [findCalendarItem, isAppleDesktopSupported, requestRecurringEventScope]
  );

  const onEventResize = async (data: EventInteractionArgs<object>) => {
    const { start, end, event } = data;
    const targetEvent = event as RBCEvent;
    const allPlanSuggestionId = targetEvent.eventId?.startsWith('all_plan_')
      ? targetEvent.eventId.replace('all_plan_', '')
      : null;
    if (allPlanSuggestionId) {
      let clampedEnd = end;
      if (dayjs(start).isSame(dayjs(end))) {
        clampedEnd = dayjs(end).add(CALENDAR_STEP_MIN, 'minute').toDate();
      }

      setSuggestAllAIPlanState((prev) => ({
        ...prev,
        schedules: prev.schedules.map((schedule, index) => {
          const scheduleId = String(schedule.id ?? `plan-${index}`);
          if (scheduleId !== allPlanSuggestionId) {
            return schedule;
          }
          return {
            ...schedule,
            startDate: dayjs(start).toISOString(),
            endDate: dayjs(clampedEnd).toISOString(),
          };
        }),
      }));
      return;
    }
    if (isTemporalAutoScheduledEvent(targetEvent.eventId, ['activity', 'energyBoost'])) {
      return;
    }

    const hasAccesToModify = checkCalendarHassAccessToModifyEvent(targetEvent.calendarId!);

    if (!hasAccesToModify) {
      setEventsSnackbar({ message: translateI18n('calendar.snackbar.noModifyAccess'), type: 'warning' });
      return;
    }

    if (dayjs(start).isAfter(dayjs(end))) {
      return;
    }

    let clampedEnd = end;
    if (dayjs(start).isSame(dayjs(end))) {
      clampedEnd = dayjs(end).add(CALENDAR_STEP_MIN, 'minute').toDate();
    }

    if (isTemporalAutoScheduledEvent(targetEvent.eventId, ['aiScheduler'])) {
      setEvents((prev) =>
        prev.map((e) =>
          e.eventId === targetEvent.eventId ? ({ ...e, start, end: clampedEnd } as RBCEvent) : e
        )
      );
      const updatedActions = aiScheduledActions.map((action) => {
        if (action.id === targetEvent.autoScheduleFrom?.id) {
          return {
            ...action,
            startDate: dayjs(start).toISOString(),
            endDate: dayjs(clampedEnd).toISOString(),
          };
        }
        return action;
      });
      setAiScheduledActions(updatedActions);
      return;
    }

    if (!targetEvent.calendarId) {
      return;
    }

    if (targetEvent.autoScheduleFrom?.from === 'meal' && targetEvent.title) {
      handleMealEventUpdate(targetEvent, dayjs(start).toDate(), dayjs(clampedEnd).toDate());
      return;
    }

    if (targetEvent.autoScheduleFrom?.from === 'sleep') {
      handleSleepEventUpdate(targetEvent, dayjs(start).toDate(), dayjs(clampedEnd).toDate());
      return;
    }

    const recurringScope = await maybeGetRecurringProviderScope(targetEvent);
    if (recurringScope === 'cancel') {
      return;
    }

    setEvents((prev) =>
      prev.map((e) =>
        e.eventId === targetEvent.eventId ? ({ ...e, start, end: clampedEnd } as RBCEvent) : e
      )
    );

    try {
      await updateDraggedEvent({
        targetEvent,
        start: dayjs(start).toDate(),
        end: dayjs(clampedEnd).toDate(),
        recurringUpdateSpan: recurringScope,
        allowUndo: !recurringScope && !isAppleEvent(targetEvent.calendarId!, findCalendarItem),
      });
      trackEventMixpanel('resize_calendar_event_by_drag');
    } catch {
      setEvents((prev) =>
        prev.map((e) =>
          e.eventId === targetEvent.eventId
            ? ({ ...e, start: targetEvent.start, end: targetEvent.end } as RBCEvent)
            : e
        )
      );
    }
  };

  const onEventDrop = async (data: EventInteractionArgs<object>) => {
    const { start, end, event } = data;
    const targetEvent = event as RBCEvent;
    const allPlanSuggestionId = targetEvent.eventId?.startsWith('all_plan_')
      ? targetEvent.eventId.replace('all_plan_', '')
      : null;
    if (allPlanSuggestionId) {
      setSuggestAllAIPlanState((prev) => ({
        ...prev,
        schedules: prev.schedules.map((schedule, index) => {
          const scheduleId = String(schedule.id ?? `plan-${index}`);
          if (scheduleId !== allPlanSuggestionId) {
            return schedule;
          }
          return {
            ...schedule,
            startDate: dayjs(start).toISOString(),
            endDate: dayjs(end).toISOString(),
          };
        }),
      }));
      return;
    }
    if (isTemporalAutoScheduledEvent(targetEvent.eventId, ['activity', 'energyBoost'])) {
      return;
    }

    const hasAccesToModify = checkCalendarHassAccessToModifyEvent(targetEvent.calendarId!);

    if (!hasAccesToModify) {
      setEventsSnackbar({ message: translateI18n('calendar.snackbar.noModifyAccess'), type: 'warning' });
      return;
    }

    if (dayjs(start).isSameOrAfter(dayjs(end))) {
      return;
    }

    if (isTemporalAutoScheduledEvent(targetEvent.eventId, ['aiScheduler'])) {
      setEvents((prev) =>
        prev.map((e) =>
          e.eventId === targetEvent.eventId ? ({ ...e, start, end } as RBCEvent) : e
        )
      );
      const updatedActions = aiScheduledActions.map((action) => {
        if (action.id === targetEvent.autoScheduleFrom?.id) {
          return {
            ...action,
            startDate: dayjs(start).toISOString(),
            endDate: dayjs(end).toISOString(),
          };
        }
        return action;
      });
      setAiScheduledActions(updatedActions);
      return;
    }

    if (!targetEvent.calendarId) {
      return;
    }

    if (targetEvent.autoScheduleFrom?.from === 'meal' && targetEvent.title) {
      handleMealEventUpdate(targetEvent, dayjs(start).toDate(), dayjs(end).toDate());
      return;
    }

    if (targetEvent.autoScheduleFrom?.from === 'sleep') {
      handleSleepEventUpdate(targetEvent, dayjs(start).toDate(), dayjs(end).toDate());
      return;
    }

    const recurringScope = await maybeGetRecurringProviderScope(targetEvent);
    if (recurringScope === 'cancel') {
      return;
    }

    setEvents((prev) =>
      prev.map((e) => (e.eventId === targetEvent.eventId ? ({ ...e, start, end } as RBCEvent) : e))
    );

    try {
      await updateDraggedEvent({
        targetEvent,
        start: dayjs(start).toDate(),
        end: dayjs(end).toDate(),
        recurringUpdateSpan: recurringScope,
        allowUndo: !recurringScope && !isAppleEvent(targetEvent.calendarId!, findCalendarItem),
      });
      trackEventMixpanel('move_calendar_event_by_drag');
    } catch {
      setEvents((prev) =>
        prev.map((e) =>
          e.eventId === targetEvent.eventId
            ? ({ ...e, start: targetEvent.start, end: targetEvent.end } as RBCEvent)
            : e
        )
      );
    }
  };

  return {
    onEventDrop,
    onEventResize,
    events,
  };
};
